/**
 * Every way a finished schedule leaves Lunchly.
 *
 * Four of these are the *product*: the Discord message is what the team
 * actually reads every morning, so its shape is treated as a contract rather
 * than as formatting - the historical messages are reproduced character for
 * character (see `docs/ARCHITECTURE.md` §4.4). CSV is for the manager's
 * spreadsheet, JSON is the re-importable backup of one week, and the printable
 * sheet is what gets pinned beside the phones.
 *
 * The five formatters are deliberately pure: given a Schedule and its deps they
 * return a string and touch nothing else. That is what lets the whole export
 * surface be tested in the node environment, where `document` does not exist.
 * Only {@link downloadFile} and {@link copyToClipboard} reach for the DOM, and
 * both degrade to a no-op/false rather than throwing when it is absent.
 */

import {
  DAY_KEYS,
  DAY_LABELS,
  dateForDayKey,
  formatDurationCompact,
  formatMinutes,
  formatShiftRange,
  formatUkLongDate,
  formatWeekCommencing,
} from '../core/time.js';
import { displayNameFor, groupBySlot } from '../core/model.js';

/** Discord's shortcode for the cloud emoji the team has always used. */
const CLOUD = ':cloud:';

/** Food marker used by the streamlined rota copy buttons. */
const FOOD = '🍽️';

/** Self-describing tag written into every JSON export so an import can verify it. */
const JSON_FORMAT = 'lunchly-schedule-v1';

/** Column order for the CSV. Stable - managers build formulas against it. */
const CSV_COLUMNS = [
  'Date',
  'Day',
  'Employee',
  'Display name',
  'Shift start',
  'Shift end',
  'Shift length',
  'Lunch time',
  'Lunch end',
  'Group',
  'Locked',
  'Manually adjusted',
  'Source',
  'Warnings',
];

/**
 * A leading `=`, `+`, `-` or `@` makes Excel/Sheets treat the cell as a formula.
 * Every exported field is checked against this and defused with a leading
 * apostrophe, because employee names and warning text are user-supplied.
 */
const FORMULA_PREFIX_RE = /^[=+\-@]/;

/* -------------------------------------------------------------------------- */
/* Shared helpers                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Resolve export options field by field rather than with a spread, so a caller
 * that passes `{ brackets: undefined }` (very easy to do when the options come
 * from a form) still gets the documented default instead of `undefined`.
 */
function resolveOptions(options) {
  const source = options ?? {};
  return {
    brackets: source.brackets !== false,
    cloudEmoji: source.cloudEmoji !== false,
    header: source.header !== false,
    joinWith: typeof source.joinWith === 'string' ? source.joinWith : ' & ',
    includeEmptyDays: source.includeEmptyDays === true,
  };
}

/** The ISO date for a day, falling back to deriving it from the week start. */
function dayDate(schedule, dayKey) {
  const explicit = schedule?.days?.[dayKey]?.date;
  if (explicit) return explicit;
  return schedule?.weekCommencing ? dateForDayKey(schedule.weekCommencing, dayKey) : '';
}

/** "Monday 27th of July 2026", degrading to "Monday" when there is no date. */
function dayTitle(schedule, dayKey) {
  return formatUkLongDate(dayDate(schedule, dayKey)) || DAY_LABELS[dayKey] || String(dayKey);
}

/** Only assignments that actually landed on a slot can be rendered anywhere. */
function placedAssignments(schedule, dayKey) {
  return (schedule?.days?.[dayKey]?.assignments ?? []).filter((assignment) =>
    Number.isFinite(assignment?.slotMinutes),
  );
}

/** Slots ascending; order within a slot is the assignment order (stable). */
function slotsForDay(schedule, dayKey) {
  return groupBySlot(placedAssignments(schedule, dayKey));
}

/** Index one day of `deps.weekShifts` by employee so lookups stay O(1). */
function shiftIndexFor(weekShifts, dayKey) {
  const index = new Map();
  for (const shift of weekShifts?.[dayKey] ?? []) {
    if (shift?.employeeId) index.set(shift.employeeId, shift);
  }
  return index;
}

/** True only for a record that carries real parsed times. */
function isRealShift(shift) {
  return Boolean(shift) && shift.kind === 'shift' && Number.isFinite(shift.startMinutes);
}

/** How long this lunch lasts, honouring the assignment then the settings. */
function lunchDurationFor(assignment, settings) {
  const onAssignment = Number(assignment?.durationMinutes);
  if (Number.isFinite(onAssignment) && onAssignment > 0) return onAssignment;
  const fromSettings = Number(settings?.lunchDurationMinutes);
  return Number.isFinite(fromSettings) && fromSettings > 0 ? fromSettings : 30;
}

/**
 * Collect the warnings that belong to one employee on one day, de-duplicated by
 * the deterministic warning id so a warning present in both the day list and the
 * week list is only reported once.
 */
function warningIndexFor(schedule, dayKey) {
  const index = new Map();
  const seen = new Set();
  const add = (warning) => {
    if (!warning?.employeeId) return;
    if (warning.dayKey && warning.dayKey !== dayKey) return;
    const id = warning.id ?? `${warning.code}:${warning.employeeId}:${warning.message}`;
    if (seen.has(id)) return;
    seen.add(id);
    const list = index.get(warning.employeeId) ?? [];
    list.push(warning.message || warning.code || '');
    index.set(warning.employeeId, list);
  };

  for (const warning of schedule?.days?.[dayKey]?.warnings ?? []) add(warning);
  for (const warning of schedule?.warnings ?? []) {
    if (warning?.dayKey === dayKey) add(warning);
  }
  return index;
}

/* -------------------------------------------------------------------------- */
/* Discord                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Render one day as the Discord message the team already recognises:
 *
 *     :cloud: Lunches | Monday 27th of July 2026 :cloud:
 *     [12:00] - Jill
 *     [13:00] - Emma & Alicia
 *
 * The exact shape matters more than it looks: people scan this at a glance on a
 * phone, so the brackets, the spaced hyphen and the two cloud emoji are all
 * preserved by default and only removed when a manager explicitly turns them
 * off. Shared slots keep the order the solver assigned them, which is the order
 * the manager sees on the day card - reordering them alphabetically here would
 * make the message disagree with the UI.
 *
 * @param {object} schedule - a Schedule from `scheduler/index.js`.
 * @param {string} dayKey - 'mon'..'sun'.
 * @param {{ rota?: object, aliasBook?: object, settings?: object, weekShifts?: object }} [deps]
 * @param {{ brackets?: boolean, cloudEmoji?: boolean, header?: boolean, joinWith?: string }} [options]
 * @returns {string} the message body, without a trailing newline.
 */
export function formatDiscordDay(schedule, dayKey, deps = {}, options = {}) {
  const opts = resolveOptions(options);
  const lines = [];

  if (opts.header) {
    const title = `Lunches | ${dayTitle(schedule, dayKey)}`;
    // No emoji means no surrounding spaces either - a stray trailing space is
    // visible when the message is quoted or copied back out of Discord.
    lines.push(opts.cloudEmoji ? `${CLOUD} ${title} ${CLOUD}` : title);
  }

  for (const slot of slotsForDay(schedule, dayKey)) {
    const names = slot.assignments
      .map((assignment) =>
        displayNameFor(deps.aliasBook, assignment.employeeId, assignment.name),
      )
      .join(opts.joinWith);
    const time = formatMinutes(slot.slotMinutes);
    lines.push(opts.brackets ? `[${time}] - ${names}` : `${time} - ${names}`);
  }

  return lines.join('\n');
}

/**
 * Render the whole week as one message, days separated by a blank line.
 *
 * Days with nothing on them are dropped by default: a weekend with no lunches
 * produces a bare header that reads like a mistake in the channel. Managers who
 * want the full seven-day skeleton can ask for it with `includeEmptyDays`.
 *
 * @param {object} schedule
 * @param {{ rota?: object, aliasBook?: object, settings?: object, weekShifts?: object }} [deps]
 * @param {{ brackets?: boolean, cloudEmoji?: boolean, header?: boolean, joinWith?: string,
 *           includeEmptyDays?: boolean }} [options]
 * @returns {string}
 */
export function formatDiscordWeek(schedule, deps = {}, options = {}) {
  const opts = resolveOptions(options);
  const blocks = [];

  for (const dayKey of DAY_KEYS) {
    const hasLunches = placedAssignments(schedule, dayKey).length > 0;
    if (!hasLunches && !opts.includeEmptyDays) continue;
    const block = formatDiscordDay(schedule, dayKey, deps, opts);
    if (block) blocks.push(block);
  }

  return blocks.join('\n\n');
}

/* -------------------------------------------------------------------------- */
/* Streamlined rota copy                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Render one day in the compact format copied from the rota cards:
 *
 *     🍽️ Lunches | Monday 27th of July 2026 🍽️
 *     [12:00] - Jill
 *     [13:00] - Emma & Alicia
 *
 * People sharing a slot are joined with ` & ` on one line, matching how a
 * supervisor reads a double lunch. The plain-text form deliberately contains
 * no asterisks: Slack's rich-text composer displays pasted markup characters
 * literally. The companion HTML formatter below supplies actual `<strong>`
 * formatting to the clipboard while this remains the clean fallback.
 */
export function formatLunchRotaDay(schedule, dayKey, deps = {}) {
  const lines = [`${FOOD} Lunches | ${dayTitle(schedule, dayKey)} ${FOOD}`];

  for (const slot of slotsForDay(schedule, dayKey)) {
    const time = formatMinutes(slot.slotMinutes);
    const names = slot.assignments.map((assignment) =>
      displayNameFor(deps.aliasBook, assignment.employeeId, assignment.name));
    lines.push(`[${time}] - ${names.join(' & ')}`);
  }

  return lines.join('\n');
}

/** Copy-ready week made from populated daily blocks separated by a blank line. */
export function formatLunchRotaWeek(schedule, deps = {}, options = {}) {
  const blocks = [];

  for (const dayKey of DAY_KEYS) {
    const hasLunches = placedAssignments(schedule, dayKey).length > 0;
    if (!hasLunches && options?.includeEmptyDays !== true) continue;
    blocks.push(formatLunchRotaDay(schedule, dayKey, deps));
  }

  return blocks.join('\n\n');
}

/** Rich clipboard counterpart: Slack preserves these `<strong>` time spans. */
export function formatLunchRotaDayHtml(schedule, dayKey, deps = {}) {
  const lines = [
    `<div>${escapeHtml(`${FOOD} Lunches | ${dayTitle(schedule, dayKey)} ${FOOD}`)}</div>`,
  ];

  for (const slot of slotsForDay(schedule, dayKey)) {
    const time = escapeHtml(formatMinutes(slot.slotMinutes));
    const names = slot.assignments.map((assignment) => escapeHtml(
      displayNameFor(deps.aliasBook, assignment.employeeId, assignment.name),
    ));
    lines.push(`<div><strong>[${time}]</strong> - ${names.join(' &amp; ')}</div>`);
  }

  return lines.join('');
}

/** Rich copy-ready week with an empty visual line between populated days. */
export function formatLunchRotaWeekHtml(schedule, deps = {}, options = {}) {
  const blocks = [];

  for (const dayKey of DAY_KEYS) {
    const hasLunches = placedAssignments(schedule, dayKey).length > 0;
    if (!hasLunches && options?.includeEmptyDays !== true) continue;
    blocks.push(formatLunchRotaDayHtml(schedule, dayKey, deps));
  }

  return blocks.join('<div><br></div>');
}

/* -------------------------------------------------------------------------- */
/* CSV                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Escape one CSV field.
 *
 * Two separate concerns, applied in order. First the spreadsheet-formula
 * defusal: a name or warning beginning `=`, `+`, `-` or `@` is executable in
 * Excel and Sheets, so it gets a leading apostrophe. Then RFC 4180 quoting for
 * anything containing a delimiter, a quote or a line break.
 */
function csvField(value) {
  let text = value === null || value === undefined ? '' : String(value);
  if (FORMULA_PREFIX_RE.test(text)) text = `'${text}`;
  if (/[",\n\r]/.test(text)) text = `"${text.replace(/"/g, '""')}"`;
  return text;
}

/**
 * One row per assigned lunch, in chronological order across the week.
 *
 * The shift columns are the reason `deps.weekShifts` exists: a manager checking
 * the schedule in a spreadsheet wants to see the lunch *beside* the shift it
 * sits in, not have to cross-reference the rota. They are left blank rather
 * than guessed when the shift records were not supplied.
 *
 * Only placed assignments appear here - people who were eligible and missed out
 * are a warning, not a row with empty lunch columns that would quietly pad a
 * COUNT() in somebody's spreadsheet.
 *
 * @param {object} schedule
 * @param {{ rota?: object, aliasBook?: object, settings?: object, weekShifts?: object }} [deps]
 * @returns {string} CSV text ending in a newline.
 */
export function toCsv(schedule, deps = {}) {
  const rows = [CSV_COLUMNS.map(csvField).join(',')];

  for (const dayKey of DAY_KEYS) {
    const shifts = shiftIndexFor(deps.weekShifts, dayKey);
    const warnings = warningIndexFor(schedule, dayKey);
    const date = dayDate(schedule, dayKey);

    for (const slot of slotsForDay(schedule, dayKey)) {
      for (const assignment of slot.assignments) {
        const shift = shifts.get(assignment.employeeId);
        const real = isRealShift(shift);
        const duration = lunchDurationFor(assignment, deps.settings);

        rows.push(
          [
            date,
            DAY_LABELS[dayKey] ?? dayKey,
            assignment.name ?? '',
            displayNameFor(deps.aliasBook, assignment.employeeId, assignment.name),
            real ? formatMinutes(shift.startMinutes) : '',
            real ? formatMinutes(shift.endMinutes) : '',
            real ? formatDurationCompact(shift.durationMinutes) : '',
            formatMinutes(assignment.slotMinutes),
            formatMinutes(assignment.slotMinutes + duration),
            assignment.group ?? '',
            assignment.locked ? 'Yes' : 'No',
            assignment.manual ? 'Yes' : 'No',
            assignment.source ?? '',
            (warnings.get(assignment.employeeId) ?? []).join('; '),
          ]
            .map(csvField)
            .join(','),
        );
      }
    }
  }

  // Trailing newline: POSIX tools and Excel both prefer a terminated last line.
  return `${rows.join('\n')}\n`;
}

/* -------------------------------------------------------------------------- */
/* JSON                                                                       */
/* -------------------------------------------------------------------------- */

/** Strip an Assignment down to the fields that describe a decision, not its rationale. */
function assignmentForJson(assignment, aliasBook) {
  return {
    employeeId: assignment.employeeId,
    name: assignment.name ?? '',
    displayName: displayNameFor(aliasBook, assignment.employeeId, assignment.name),
    dayKey: assignment.dayKey,
    slotMinutes: assignment.slotMinutes,
    time: formatMinutes(assignment.slotMinutes),
    durationMinutes: assignment.durationMinutes ?? null,
    group: assignment.group ?? null,
    locked: assignment.locked === true,
    manual: assignment.manual === true,
    source: assignment.source ?? 'auto',
    score: assignment.score ?? null,
    overrideReason: assignment.overrideReason ?? '',
    acknowledgedWarnings: [...(assignment.acknowledgedWarnings ?? [])],
  };
}

/** Keep the parts of a warning a human or a re-import needs, drop the rest. */
function warningForJson(warning) {
  return {
    id: warning.id ?? null,
    code: warning.code ?? null,
    severity: warning.severity ?? 'warning',
    employeeId: warning.employeeId ?? null,
    slotMinutes: warning.slotMinutes ?? null,
    message: warning.message ?? '',
    acknowledged: warning.acknowledged === true,
  };
}

/**
 * Pretty-printed, self-describing JSON for one week.
 *
 * This is the format a manager mails to a colleague or keeps as a record, so it
 * is written to be read as much as parsed: the `format` tag lets an importer
 * refuse a file that is not ours, and every assignment carries a human `time`
 * beside its `slotMinutes`.
 *
 * Two deliberate omissions. Coverage buckets are left out because they are
 * recomputed from the shifts on load and would triple the file size, and each
 * assignment's `explanation` is left out because it is generated commentary
 * about one particular solver run, not state. Everything a human actually
 * decided - locks, manual moves, override reasons, acknowledged warnings - is
 * kept, so a round trip loses no judgement.
 *
 * @param {object} schedule
 * @param {{ rota?: object, aliasBook?: object, settings?: object, weekShifts?: object }} [deps]
 * @returns {string} JSON text, 2-space indented.
 */
export function toJson(schedule, deps = {}) {
  const aliasBook = deps.aliasBook;
  const days = {};

  for (const dayKey of DAY_KEYS) {
    const day = schedule?.days?.[dayKey];
    days[dayKey] = {
      dayKey,
      date: dayDate(schedule, dayKey),
      solved: day?.solved !== false,
      assignments: slotsForDay(schedule, dayKey).flatMap((slot) =>
        slot.assignments.map((assignment) => assignmentForJson(assignment, aliasBook)),
      ),
      unassigned: (day?.unassigned ?? []).map((record) => ({
        employeeId: record.employeeId,
        name: record.name ?? '',
        reason: record.reason ?? null,
        reasonText: record.reasonText ?? '',
      })),
      warnings: (day?.warnings ?? []).map(warningForJson),
    };
  }

  const payload = {
    format: JSON_FORMAT,
    weekCommencing: schedule?.weekCommencing ?? '',
    // The moment the *schedule* was solved, not the moment it was exported - 
    // that keeps two exports of an untouched week byte-identical.
    generatedAt: schedule?.generatedAt ?? null,
    settings: deps.settings ?? null,
    days,
    aliases: {
      entries: { ...(aliasBook?.entries ?? {}) },
      unresolved: (aliasBook?.unresolved ?? []).map((item) => ({ ...item })),
    },
  };

  return JSON.stringify(payload, null, 2);
}

/* -------------------------------------------------------------------------- */
/* Printable HTML                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Escape text for HTML interpolation.
 *
 * Local rather than imported because this module must stay usable outside the
 * browser, and because the printable sheet is the one place in Lunchly that
 * builds markup as a string instead of through `ui/dom.js`.
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** "3 lunches" / "1 lunch". */
function lunchCountLabel(count) {
  return `${count} ${count === 1 ? 'lunch' : 'lunches'}`;
}

/** The stylesheet for the print sheet: A4, ink-frugal, no external assets. */
const PRINT_STYLES = `
  @page { size: A4 portrait; margin: 16mm 15mm 14mm; }

  * { box-sizing: border-box; }

  body {
    margin: 0;
    background: #ffffff;
    color: #000000;
    font: 400 10.5pt/1.55 "Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-variant-numeric: tabular-nums lining-nums;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .sheet { max-width: 180mm; margin: 0 auto; padding: 12mm 10mm 10mm; }

  .masthead { border-bottom: 1.4pt solid #000000; padding-bottom: 4mm; margin-bottom: 8mm; }
  .masthead__brand {
    margin: 0;
    font-size: 7.5pt;
    font-weight: 600;
    letter-spacing: 0.26em;
    text-transform: uppercase;
    color: #4a4a4a;
  }
  .masthead__title { margin: 2.5mm 0 0; font-size: 21pt; font-weight: 600; letter-spacing: -0.015em; }
  .masthead__meta { margin: 2mm 0 0; font-size: 9pt; color: #3d3d3d; }
  .masthead__source { margin: 0.8mm 0 0; font-size: 8.5pt; color: #6a6a6a; font-style: italic; }

  .day {
    margin: 0 0 7mm;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  /* A day with nothing on it is an acknowledgement, not a section. */
  .day--empty { margin-bottom: 4mm; }
  .day--empty .day__head { border-bottom-width: 0.3pt; margin-bottom: 1.2mm; }
  .day--empty .day__name { font-size: 11pt; font-weight: 500; color: #4a4a4a; }
  .day__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8mm;
    border-bottom: 0.6pt solid #000000;
    padding-bottom: 1.6mm;
    margin-bottom: 2.2mm;
  }
  .day__name { margin: 0; font-size: 12.5pt; font-weight: 600; letter-spacing: -0.005em; }
  .day__count {
    margin: 0;
    font-size: 7.5pt;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #4a4a4a;
    white-space: nowrap;
  }

  .slots { list-style: none; margin: 0; padding: 0; }
  .slot {
    display: grid;
    grid-template-columns: 17mm minmax(0, 1fr) auto;
    gap: 0 5mm;
    align-items: baseline;
    padding: 1.15mm 0;
    border-bottom: 0.25pt solid #d2d2d2;
  }
  .slot:last-child { border-bottom: 0; }
  /* A blank line where the day group hands over to the evening group. */
  .slot--group-change { margin-top: 3.2mm; }
  .slot__time { font-weight: 600; font-size: 10.5pt; }
  .slot__names { font-size: 10.5pt; }
  .slot__shift { font-size: 8.5pt; color: #5f5f5f; white-space: nowrap; }

  .day__empty { margin: 0.5mm 0 0; font-size: 9pt; font-style: italic; color: #5f5f5f; }

  .day__notes { margin: 2.6mm 0 0; padding-left: 3mm; border-left: 1.6pt solid #b8b8b8; }
  .day__notes-title {
    margin: 0 0 0.8mm;
    font-size: 7.5pt;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #4a4a4a;
  }
  .notes { list-style: none; margin: 0; padding: 0; font-size: 8.5pt; color: #3d3d3d; }
  .notes li { margin: 0 0 0.5mm; }
  .notes__name { font-weight: 600; }
  .notes__why { color: #5f5f5f; }

  .colophon {
    margin-top: 9mm;
    padding-top: 2.5mm;
    border-top: 0.6pt solid #000000;
    font-size: 8pt;
    color: #5f5f5f;
    display: flex;
    justify-content: space-between;
    gap: 6mm;
  }
  .colophon span { white-space: nowrap; }

  @media print {
    .sheet { padding: 0; max-width: none; }
    .day { orphans: 3; widows: 3; }
  }

  @media screen {
    body { background: #f2f3f5; padding: 8mm 0; }
    .sheet { background: #ffffff; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18); }
  }
`;

/** One `<li>` per occupied slot, with a hairline break where the group changes. */
function renderSlotRows(slots, deps) {
  let previousGroup = null;
  return slots
    .map((slot) => {
      const group = slot.assignments[0]?.group ?? null;
      const changed = previousGroup !== null && group !== previousGroup;
      previousGroup = group;

      // Ampersand, not a comma list, so the printed sheet reads the same way as
      // the Discord message the team already has open on their phones.
      const names = slot.assignments
        .map((assignment) =>
          escapeHtml(displayNameFor(deps.aliasBook, assignment.employeeId, assignment.name)),
        )
        .join(' &amp; ');

      const shifts = slot.assignments
        .map((assignment) => deps.shiftIndex.get(assignment.employeeId))
        .filter(isRealShift)
        .map((shift) => formatShiftRange(shift.startMinutes, shift.endMinutes));
      const shiftText = [...new Set(shifts)].join(' · ');

      return [
        `        <li class="slot${changed ? ' slot--group-change' : ''}">`,
        `          <span class="slot__time">${escapeHtml(formatMinutes(slot.slotMinutes))}</span>`,
        `          <span class="slot__names">${names}</span>`,
        `          <span class="slot__shift">${escapeHtml(shiftText)}</span>`,
        '        </li>',
      ].join('\n');
    })
    .join('\n');
}

/** The "eligible but not placed" footnote - the thing a manager must act on. */
function renderNotes(unassigned) {
  if (!unassigned.length) return '';
  const items = unassigned
    .map((record) => {
      const why = record.reasonText || record.reason || 'No slot available.';
      return [
        `          <li><span class="notes__name">${escapeHtml(record.name || record.employeeId)}</span>`,
        `            <span class="notes__why">${escapeHtml(why)}</span></li>`,
      ].join('\n');
    })
    .join('\n');

  return [
    '      <div class="day__notes">',
    '        <p class="day__notes-title">Not scheduled</p>',
    '        <ul class="notes">',
    items,
    '        </ul>',
    '      </div>',
  ].join('\n');
}

/**
 * A complete standalone HTML document for printing on A4.
 *
 * Standalone is the point: this string is written into a new window or saved to
 * disk, so it carries its own doctype, its own inline stylesheet and no
 * external asset of any kind - a print sheet that depends on a stylesheet the
 * browser cannot fetch prints as unstyled soup.
 *
 * It is set in black on white with hairline rules and tabular figures because
 * it is read at arm's length beside a phone, and every day section is marked
 * `page-break-inside: avoid` so no day is ever split across two sheets. All
 * seven days are listed, empty ones included as a single line, so nobody has to
 * wonder whether Sunday was forgotten or simply had no lunches.
 *
 * Deterministic by design: nothing here reads the current clock, so exporting
 * an untouched week twice produces identical output.
 *
 * @param {object} schedule
 * @param {{ rota?: object, aliasBook?: object, settings?: object, weekShifts?: object }} [deps]
 * @returns {string} a full HTML document.
 */
export function buildPrintableHtml(schedule, deps = {}) {
  const weekCommencing = schedule?.weekCommencing ?? '';
  const weekLabel = formatWeekCommencing(weekCommencing) || 'Lunch schedule';
  const lunchDuration = lunchDurationFor(null, deps.settings);

  const sections = [];
  let totalLunches = 0;
  let daysWithLunches = 0;

  for (const dayKey of DAY_KEYS) {
    const day = schedule?.days?.[dayKey];
    const slots = slotsForDay(schedule, dayKey);
    const count = slots.reduce((sum, slot) => sum + slot.assignments.length, 0);
    totalLunches += count;
    if (count > 0) daysWithLunches += 1;

    const body = count > 0
      ? [
        '      <ol class="slots">',
        renderSlotRows(slots, { aliasBook: deps.aliasBook, shiftIndex: shiftIndexFor(deps.weekShifts, dayKey) }),
        '      </ol>',
      ].join('\n')
      : '      <p class="day__empty">No lunches scheduled.</p>';

    const notes = renderNotes(day?.unassigned ?? []);

    sections.push(
      [
        `    <section class="day${count === 0 ? ' day--empty' : ''}">`,
        '      <div class="day__head">',
        `        <h2 class="day__name">${escapeHtml(dayTitle(schedule, dayKey))}</h2>`,
        // The count would only repeat "No lunches scheduled." on an empty day.
        count > 0 ? `        <p class="day__count">${escapeHtml(lunchCountLabel(count))}</p>` : '',
        '      </div>',
        body,
        notes,
        '    </section>',
      ]
        .filter((part) => part !== '')
        .join('\n'),
    );
  }

  const meta = [
    weekLabel,
    `${lunchCountLabel(totalLunches)} across ${daysWithLunches} ${daysWithLunches === 1 ? 'day' : 'days'}`,
    `${lunchDuration}-minute breaks`,
  ].join(' · ');

  const source = deps.rota?.notes
    ? `      <p class="masthead__source">Source: ${escapeHtml(deps.rota.notes)}</p>`
    : '';

  const generated = schedule?.generatedAt
    ? `<span>Generated ${escapeHtml(String(schedule.generatedAt).slice(0, 10))}</span>`
    : '<span>Lunchly</span>';

  return [
    '<!doctype html>',
    '<html lang="en-GB">',
    '<head>',
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<title>Lunch schedule - ${escapeHtml(weekLabel)}</title>`,
    '<style>',
    PRINT_STYLES.trim(),
    '</style>',
    '</head>',
    '<body>',
    '  <main class="sheet">',
    '    <header class="masthead">',
    '      <p class="masthead__brand">Lunchly</p>',
    '      <h1 class="masthead__title">Lunch schedule</h1>',
    `      <p class="masthead__meta">${escapeHtml(meta)}</p>`,
    source,
    '    </header>',
    sections.join('\n'),
    '    <footer class="colophon">',
    '      <span>Smarter lunches. Better coverage.</span>',
    `      ${generated}`,
    '    </footer>',
    '  </main>',
    '</body>',
    '</html>',
    '',
  ]
    .filter((part) => part !== '')
    .join('\n');
}

/* -------------------------------------------------------------------------- */
/* Browser side-effects                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Save a string to the user's disk.
 *
 * Lunchly has no backend, so "download" means: wrap the text in a Blob, mint an
 * object URL, click a detached anchor and let the browser do the rest. The URL
 * is revoked on the next tick rather than immediately because Safari and older
 * Firefox abort the download if the URL dies inside the same task as the click.
 *
 * A no-op (with a warning) when there is no document, so a node-side caller
 * gets a diagnostic instead of a ReferenceError.
 *
 * @param {string} filename - suggested name, e.g. `lunchly-2026-07-27.csv`.
 * @param {string} content
 * @param {string} [mimeType]
 */
export function downloadFile(filename, content, mimeType = 'text/plain;charset=utf-8') {
  if (typeof document === 'undefined' || typeof URL === 'undefined') {
    console.warn('[lunchly] downloadFile needs a browser document; skipping.');
    return;
  }

  const blob = new Blob([content ?? ''], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename || 'lunchly-export.txt';
  anchor.rel = 'noopener';
  anchor.style.display = 'none';

  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Legacy clipboard path: a hidden textarea plus `document.execCommand('copy')`.
 * Still needed because the async Clipboard API is unavailable on insecure
 * origins (a LAN IP, `file://`) and rejects when the document is not focused.
 * The caller's own selection is captured and restored so copying the Discord
 * message does not wipe whatever text they had highlighted.
 */
function copyWithTextarea(text) {
  if (typeof document === 'undefined') return false;

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.setAttribute('aria-hidden', 'true');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  textarea.style.left = '0';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);

  const selection = typeof document.getSelection === 'function' ? document.getSelection() : null;
  const previousRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  let copied = false;
  try {
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    copied = document.execCommand('copy') === true;
  } catch (error) {
    // `copied` is still false - the assignment above never ran.
    console.warn('[lunchly] execCommand copy failed', error);
  } finally {
    textarea.remove();
    if (selection && previousRange) {
      selection.removeAllRanges();
      selection.addRange(previousRange);
    }
  }

  return copied;
}

/**
 * Copy text to the clipboard, reporting success instead of throwing.
 *
 * "Copy the Discord message" is the single most-used button in the app, so it
 * must never surface a stack trace: every failure path resolves to `false` and
 * the caller shows a toast offering the textarea to copy from by hand. The
 * modern API is tried first and the legacy path is the fallback, not the
 * reverse, because `execCommand` is deprecated and steals focus.
 *
 * @param {string} text
 * @param {{ html?: string }} [options] optional rich representation for Slack
 * @returns {Promise<boolean>} true when the clipboard now holds the text.
 */
export async function copyToClipboard(text, options = {}) {
  const value = text === null || text === undefined ? '' : String(text);
  const html = typeof options?.html === 'string' ? options.html : '';

  if (html
    && typeof navigator !== 'undefined'
    && navigator.clipboard?.write
    && typeof globalThis.ClipboardItem === 'function'
    && typeof globalThis.Blob === 'function') {
    try {
      const item = new globalThis.ClipboardItem({
        'text/plain': new Blob([value], { type: 'text/plain' }),
        'text/html': new Blob([html], { type: 'text/html' }),
      });
      await navigator.clipboard.write([item]);
      return true;
    } catch (error) {
      console.warn('[lunchly] rich clipboard copy failed, trying plain text', error);
    }
  }

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (error) {
      // Permission denied, unfocused document, insecure origin - all recoverable.
      console.warn('[lunchly] clipboard API refused, trying the legacy path', error);
    }
  }

  return copyWithTextarea(value);
}
